import context, { ServiceModel } from '@truckdown/systems';
import { defineComponent, ref, Ref, computed, watch} from 'vue';
import editBanner from '../EditBanner/EditBanner.vue';
import analytics from './BannerAnalytics/BannerAnalytics.vue';
import { BannerModel, BannerStatus, IBannerEditManager, UpdateBannerStatus } from '@truckdown/locations';
import { isCsrOrAdmin, showMessage } from '../../Utilities/utilities';
interface IDisplayBanner {
    id: string;
    status: string;
    statusClass: string;
    type: string;
    accountId: string;
    imageId: string;
    websiteAddress: string;
    services: string[];
    activeSubscriptions: [];
    first5Services: string[];
    location: string;
}

interface IProps {
    banner: BannerModel;
    analytics: any;
    viewDetails: boolean;
    accountNumber: string | undefined;
}

interface IData {
    showAllServices: Ref<boolean>,
    idToDelete: Ref<string>,
    showEdit: Ref<boolean>
    showReports: Ref<boolean>,
    showDeleteModal: Ref<boolean>,
    viewBanner: Ref<BannerModel>,
    updateStatusModel: Ref<UpdateBannerStatus>
}

function mapServices(services: string[], lookup: ServiceModel[]): string[] {
    const lookupMap = new Map(
        lookup.map(s => [s.code, s.name])
    );

    return services.map(code => lookupMap.get(code) || code);
}

function getCountry(country: string): string {
    return country == "CA" ? "Canada" : "USA"
}

const getData = function (props: IProps): IData {
    return {
        showAllServices: ref<boolean>(false),
        idToDelete: ref<string>(''),
        showEdit: ref<boolean>(false),
        showReports: ref<boolean>(false),
        showDeleteModal: ref<boolean>(false),
        viewBanner: ref<BannerModel>(props.banner),
        updateStatusModel: ref<UpdateBannerStatus>({
            bannerId: props.banner.id,
            status: BannerStatus.Inactive
        })
    }
}


const getMethods = function (props: IProps, data: IData, ctx: any) {

    return {
        toggleServices: function () {
            data.showAllServices.value = !data.showAllServices.value;
        },
        openEdit: function () {
            if (data.viewBanner.value.activeSubscriptions?.length == 0) {
                data.showEdit.value = true;
            } else {
                showMessage("A banner that has an active subscription cannot be modified.", "warning", true, true);
            }
        },
        cancelEdit: function () {
            data.showEdit.value = false;
        },
        submitEdit: function (banner: BannerModel) {
           
            data.showEdit.value = false;
            data.viewBanner.value = banner;
            ctx.emit('update:banner', banner);
            showMessage("Banner was successfully saved", "success", true, true);
        },
        openDelete: function (id: string) {
            if (data.viewBanner.value.activeSubscriptions?.length == 0) {
                data.idToDelete.value = id;
                data.showDeleteModal.value = true;
            } else {
                showMessage("A banner that has an active subscription cannot be deleted.", "warning", true, true);
            }
        },
        openDetails: function (ev: Event, id: string) {
            window.location.href = `/listings/admin/view-banner/${id}`;
        },
        openReports: function (id: string) {
            data.showReports.value = true;
        },
        closeDeleteModal: function () {
            data.showDeleteModal.value = false;
            data.idToDelete.value = "";
        },
        confirmDelete: async function () {
            if (data.idToDelete.value) {
                try {
                    const manager = await context.getService<IBannerEditManager>('IBannerEditManager');
                    await manager.remove(data.idToDelete.value);
                    data.viewBanner.value.status = BannerStatus.Inactive;
                    data.idToDelete.value = "";
                    data.showDeleteModal.value = false;
                    if (props.viewDetails && props.accountNumber) {
                        window.location.href = `/listings/banners/admin/${props.accountNumber}`;
                    }
                  
                    ctx.emit('update:banner', data.viewBanner.value);
                    showMessage(`The banner was removed.`, "success", true, true);
                } catch (error) {
                    console.error('An error occured while removing the banner:', error);
                }
            }
           
        },
        approveBanner: async function () {
            data.updateStatusModel.value.status = BannerStatus.Active;
            try {
                const manager = await context.getService<IBannerEditManager>('IBannerEditManager');
                let result = await manager.updateStatus(data.updateStatusModel.value);
                if (result && result.ok) {
                    data.viewBanner.value.status = data.updateStatusModel.value.status;
                    showMessage(`The banner was approved.`, "success", true, true);
                    ctx.emit('update:banner', data.viewBanner.value);
                } else {
                    showMessage(`An error occured while approving the banner.`, "danger", true, true);
                }
            } catch (error) {
                console.error('An error occured while approving the banner:', error);
            }
        }
    }
}

const getComputed = function (props: any, data: IData) {
    const getStatus = function () {
        const baseStatus =
            data.viewBanner.value.status === BannerStatus.PendingApproval
                ? "Pending Approval"
                : BannerStatus[data.viewBanner.value.status];

        const notPublished =
            data.viewBanner.value.activeSubscriptions?.length === 0
                ? " (Not Published)"
                : "";

        return baseStatus + notPublished;
    };

    return {
        analyticsLink: computed(() => {
            return `/analytics/admin/ba/${props.banner.id}`
        }),

        displayBanner: computed(() => {
            const banner = props.banner;

            const mappedServices = mapServices(
                banner.bannerServices || [],
                props.allServices || []
            );

            return {
                id: banner.id,
                status: getStatus(),
                statusClass:
                    banner.status == BannerStatus.Active
                        ? "success td-badge"
                        : "primary td-badge",
                type: banner.regionCode ? "Regional" : "National",
                accountId: banner.accountId,
                imageId: banner.imageId,
                websiteAddress: banner.websiteAddress,
                location: `${banner.regionCode ? banner.regionCode + ', ' : ''}${getCountry(banner.countryCode)}`,
                first5Services: mappedServices.slice(0, props.servicesNumber),
                services: mappedServices,
                activeSubscriptions: banner.activeSubscriptions
            };
        }),
        hasNoSubs: computed(() => {
            return data.viewBanner.value.activeSubscriptions?.length == 0; 
        }),
        showApproveBtn: computed(() => {
            return isCsrOrAdmin() && data.viewBanner.value.status == BannerStatus.PendingApproval;
        }),
        showDetailsBtn: computed(() => {
            return props.isAdmin && !props.viewDetails;
        })
      
    };
}

export default defineComponent({
    name: "banner",
    emits: ["update:banner"],
    components: {
        "edit-banner": editBanner,
        "banner-analytics": analytics
    },
    props: {
        banner: {
            type: Object as () => BannerModel,
            required: true
        },
        servicesNumber: {
            type: Number,
            default: 5
        },
        viewDetails: {
            type: Boolean,
            default: false
        },
        allServices: {
            type: Array as () => ServiceModel[],
            default: []
        },
        analytics: Object,
        accountNumber: {
            type: String,
            required: false
        },
        isAdmin: {
            type: Boolean,
            default: false
        }
       
    },
    setup: function (props, ctx) {
        let data = getData(props);
        let computed = getComputed(props, data);
        let methods = getMethods(props, data, ctx);

        watch(() => props.banner, (newVal, oldVal) => {
            if (newVal) {
                console.log("banner chanegd", newVal.imageId, ' ', oldVal.imageId)
                data.viewBanner.value = newVal;
            }
        });

        return {
            ...data,
            ...computed,
            ...methods
        };
    }
});